import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../utils/apiService';
import { useUser } from '../contexts/UserContext';
import { ACCESS_FLAGS_STALE_EVENT, extractAccessFlagsFromApi } from '../utils/userAccessFlags';

/** Fallback poll — SignalR AccessFlagsChanged is the primary (near-instant) signal. */
const POLL_MS = 15000;

/**
 * Keeps userData.isSuspended / suspendedReason in sync with the DB after login, so an admin
 * suspend/unsuspend shows (or clears) the Account Restricted panel without re-login.
 * Triggers: mount, every route change, SignalR AccessFlagsChanged, any API answering
 * "Account is suspended", tab focus / visibility, and a 15s fallback poll.
 */
export function useAccessFlagsSync(enabled) {
  const { applyAccessFlags } = useUser();
  const { pathname } = useLocation();
  const inflightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled || inflightRef.current) return;
    inflightRef.current = true;
    try {
      const res = await apiService.getAccessFlags();
      if (res?.success && res.data) applyAccessFlags(extractAccessFlagsFromApi(res.data));
    } catch {
      // 401 handled by apiService → logout flow
    } finally {
      inflightRef.current = false;
    }
  }, [enabled, applyAccessFlags]);

  // Re-check on every navigation (menu click, typed URL) — covers a missed SignalR push.
  useEffect(() => {
    refresh();
  }, [pathname, refresh]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onStale = (event) => {
      const pushed = event?.detail?.flags;
      if (pushed) applyAccessFlags(extractAccessFlagsFromApi(pushed));
      else refresh();
    };
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener(ACCESS_FLAGS_STALE_EVENT, onStale);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(refresh, POLL_MS);

    return () => {
      window.removeEventListener(ACCESS_FLAGS_STALE_EVENT, onStale);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [enabled, refresh, applyAccessFlags]);
}
